import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const OUTPUT_TYPES = [
  { id: 'video', label: 'Video', icon: 'Video', color: '#10B981', desc: 'Cinematic video scene' },
  { id: 'animation', label: 'Animation', icon: 'Film', color: 'var(--color-accent)', desc: 'Animated sequence' },
  { id: 'game', label: 'Game Level', icon: 'Gamepad2', color: 'var(--color-primary)', desc: 'Playable game level' },
  { id: 'music', label: 'Music', icon: 'Music', color: '#F43F5E', desc: 'Background soundtrack' },
];

export default function StoryMultiOutput({ onGenerateMultiple, isGenerating, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [storyPrompt, setStoryPrompt] = useState('');
  const [selectedOutputs, setSelectedOutputs] = useState(['video', 'game']);
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);

  const toggleOutput = (id) => {
    setSelectedOutputs(prev =>
      prev?.includes(id) ? prev?.filter(o => o !== id) : [...prev, id]
    );
  };

  const handleGenerate = async () => {
    if (!storyPrompt?.trim() || selectedOutputs?.length === 0 || isRunning) return;
    setIsRunning(true);
    setResults([]);

    for (const outputId of selectedOutputs) {
      await new Promise(r => setTimeout(r, 800));
      const outputType = OUTPUT_TYPES?.find(o => o?.id === outputId);
      setResults(prev => [...prev, { id: outputId, label: outputType?.label, status: 'generating' }]);
      await new Promise(r => setTimeout(r, 1200));
      setResults(prev => prev?.map(r => r?.id === outputId ? { ...r, status: 'done' } : r));
    }

    setIsRunning(false);
    onGenerateMultiple?.(storyPrompt, selectedOutputs);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold transition-all hover:opacity-80"
        style={{ borderColor: 'rgba(139,92,246,0.4)', background: 'rgba(139,92,246,0.08)', color: 'var(--color-secondary)', fontFamily: 'var(--font-heading)' }}
      >
        <Icon name="Layers" size={13} color="var(--color-secondary)" />
        Story → Multi-Output
        <Icon name="ChevronDown" size={12} color="var(--color-secondary)" />
      </button>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden animate-fade-in-up" style={{ background: 'var(--color-card)', border: '1px solid rgba(139,92,246,0.3)', boxShadow: 'var(--shadow-md)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(139,92,246,0.06)' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-secondary)' }}>
            <Icon name="Layers" size={14} color="white" />
          </div>
          <div>
            <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Story → Multi-Output</p>
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>One prompt, multiple creations</p>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
          <Icon name="ChevronUp" size={13} color="var(--color-muted-foreground)" />
        </button>
      </div>

      <div className="p-3 flex flex-col gap-3">
        <textarea
          value={storyPrompt}
          onChange={(e) => setStoryPrompt(e?.target?.value)}
          placeholder='e.g. "A boy discovers a glowing portal in a forest"'
          rows={2}
          disabled={isRunning || disabled}
          className="w-full resize-none rounded-xl border p-3 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
          style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
        />

        {/* Output type selector */}
        <div>
          <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Generate as:</p>
          <div className="grid grid-cols-2 gap-1.5">
            {OUTPUT_TYPES?.map((type) => {
              const selected = selectedOutputs?.includes(type?.id);
              return (
                <button
                  key={type?.id}
                  onClick={() => toggleOutput(type?.id)}
                  disabled={isRunning || disabled}
                  className="flex items-center gap-2 p-2 rounded-xl border text-xs transition-all disabled:opacity-40"
                  style={{
                    borderColor: selected ? type?.color : 'var(--color-border)',
                    background: selected ? `${type?.color}12` : 'var(--color-muted)',
                    color: selected ? type?.color : 'var(--color-muted-foreground)',
                  }}
                >
                  <Icon name={selected ? 'CheckSquare' : 'Square'} size={12} color={selected ? type?.color : 'var(--color-muted-foreground)'} />
                  <Icon name={type?.icon} size={12} color={selected ? type?.color : 'var(--color-muted-foreground)'} />
                  <span style={{ fontFamily: 'var(--font-caption)' }}>{type?.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results */}
        {results?.length > 0 && (
          <div className="flex flex-col gap-1">
            {results?.map((result) => (
              <div key={result?.id} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'var(--color-muted)' }}>
                {result?.status === 'generating' ? (
                  <Icon name="Loader" size={12} color="var(--color-primary)" />
                ) : (
                  <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
                )}
                <span className="text-xs" style={{ color: result?.status === 'done' ? 'var(--color-success)' : 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
                  {result?.label} {result?.status === 'generating' ? 'generating...' : 'ready!'}
                </span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={!storyPrompt?.trim() || selectedOutputs?.length === 0 || isRunning || disabled}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-40"
          style={{ background: 'var(--color-secondary)', color: 'white', fontFamily: 'var(--font-heading)' }}
        >
          {isRunning ? <Icon name="Loader" size={13} color="white" /> : <Icon name="Layers" size={13} color="white" />}
          {isRunning ? `Generating ${selectedOutputs?.length} outputs...` : `Generate ${selectedOutputs?.length} Output${selectedOutputs?.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
}
